// ── AgentModelSettingsPage ──────────────────────────────────────────────────
//
// CFG.LLM.A1 — Per-agent model selector + change governance UI.
//
// Shows one card per PHASE_AGENT (data-driven from shared package — the UI
// never hardcodes the agent count or list). Each card shows the current
// active model, a dropdown to propose a new one, and any pending proposal
// with approve/reject buttons visible to admins.

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import PasswordInput from '../components/PasswordInput';
import { apiFetch } from '../lib/api';
import { HelpPopover } from '../components/help';
import { useMyTenant, isAdminRole } from '../hooks/useMyTenant';
import {
  AVAILABLE_MODELS,
  PHASE_AGENTS,
  type AvailableModel,
  type PhaseAgentType,
} from '@blueprint/shared';

// ── Types that mirror the API response ─────────────────────────────────────

interface PendingChangeSummary {
  changeId: string;
  fromModel: string | null;
  toModel: AvailableModel;
  /** users.name of the proposer, or null if the user has been deleted.
   *  The API intentionally does NOT expose the raw proposer UUID — the
   *  shape is name/email only so internal IDs never leak to the UI. */
  proposedByName: string | null;
  /** users.email of the proposer (fallback when name is null). */
  proposedByEmail: string | null;
  proposedAt: string;
}

/**
 * Resolve a human-readable label for the proposer. Prefers display name,
 * falls back to email, then to "(deleted user)" — per product spec the raw
 * proposedBy UUID must never appear in the UI.
 */
function proposerLabel(p: PendingChangeSummary): string {
  return p.proposedByName ?? p.proposedByEmail ?? '(deleted user)';
}

interface AgentRow {
  agentType: PhaseAgentType;
  phase: number;
  name: string;
  core: boolean;
  activeModel: AvailableModel;
  source: 'configured' | 'default';
  updatedAt: string | null;
  pendingChange: PendingChangeSummary | null;
  // CFG.LLM.A2 — BYOLLM. Endpoint URL is visible (the user needs to see it
  // to verify what's configured), but the API key is exposed only as a
  // boolean — the plaintext or encrypted value is never returned by the API.
  byollmEndpoint: string | null;
  hasByollmKey: boolean;
}

interface ListResponse {
  tenantId: string;
  availableModels: readonly AvailableModel[];
  agents: AgentRow[];
}

// ── Helpers ─────────────────────────────────────────────────────────────────

const MODEL_LABEL: Record<AvailableModel, string> = {
  'claude-opus-4-6':           'Claude Opus 4.6 — deepest reasoning',
  'claude-sonnet-4-6':         'Claude Sonnet 4.6 — balanced (default)',
  'claude-haiku-4-5-20251001': 'Claude Haiku 4.5 — fastest / cheapest',
};

function modelLabel(m: string): string {
  return (MODEL_LABEL as Record<string, string>)[m] ?? m;
}

// ── Styles (reuse design tokens from SettingsPage) ─────────────────────────

const cardStyle: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md)',
  padding: 20,
  marginBottom: 16,
};

const labelStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: 12,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
};

const selectStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: 14,
  padding: '8px 10px',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg)',
  color: 'var(--text)',
  minWidth: 280,
};

const primaryButton: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontSize: 13,
  fontWeight: 600,
  color: 'white',
  background: 'var(--primary)',
  border: 'none',
  padding: '8px 16px',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
};

const ghostButton: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--text-secondary)',
  background: 'transparent',
  border: '1px solid var(--border)',
  padding: '8px 16px',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
};

// ── Component ───────────────────────────────────────────────────────────────

export default function AgentModelSettingsPage() {
  const { myTenant, loading: tenantLoading } = useMyTenant();
  const isAdmin = isAdminRole(myTenant?.role);

  const [rows, setRows] = useState<AgentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Per-agent draft state (the model the user is about to propose).
  const [drafts, setDrafts] = useState<Record<string, AvailableModel>>({});
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [flash, setFlash] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  // CFG.LLM.A2 — BYOLLM per-agent UI state.
  //   byollmExpanded — which agent's "Custom endpoint" panel is open
  //   byollmEndpointDraft / byollmKeyDraft — the in-progress edit values
  // The plaintext key only ever lives in this client-side state; the moment
  // the user clicks Save it goes to PATCH /:agentType/byollm and the server
  // encrypts it. After save we clear the draft so the password input goes
  // back to its empty (masked) state.
  const [byollmExpanded, setByollmExpanded] = useState<Record<string, boolean>>({});
  const [byollmEndpointDraft, setByollmEndpointDraft] = useState<Record<string, string>>({});
  const [byollmKeyDraft, setByollmKeyDraft] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<ListResponse>('/api/config/agents');
      setRows(data.agents);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load agent model config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Auto-clear flash messages after 4s
  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 4000);
    return () => clearTimeout(t);
  }, [flash]);

  async function propose(agentType: PhaseAgentType) {
    const toModel = drafts[agentType];
    if (!toModel) return;
    setBusy((b) => ({ ...b, [agentType]: true }));
    try {
      await apiFetch(`/api/config/agents/${agentType}/propose`, {
        method: 'POST',
        body: JSON.stringify({ toModel }),
      });
      setFlash({ kind: 'ok', text: `Proposal submitted for ${agentType} — awaiting approval.` });
      setDrafts((d) => {
        const next = { ...d };
        delete next[agentType];
        return next;
      });
      await load();
    } catch (err) {
      setFlash({
        kind: 'err',
        text: err instanceof Error ? err.message : 'Failed to propose change',
      });
    } finally {
      setBusy((b) => ({ ...b, [agentType]: false }));
    }
  }

  async function approve(changeId: string, agentType: string) {
    setBusy((b) => ({ ...b, [changeId]: true }));
    try {
      await apiFetch(`/api/config/agents/changes/${changeId}/approve`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      setFlash({ kind: 'ok', text: `Change approved for ${agentType}.` });
      await load();
    } catch (err) {
      setFlash({
        kind: 'err',
        text: err instanceof Error ? err.message : 'Failed to approve change',
      });
    } finally {
      setBusy((b) => ({ ...b, [changeId]: false }));
    }
  }

  async function reject(changeId: string, agentType: string) {
    const reason = window.prompt('Reason for rejecting this change:');
    if (!reason) return;
    setBusy((b) => ({ ...b, [changeId]: true }));
    try {
      await apiFetch(`/api/config/agents/changes/${changeId}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
      setFlash({ kind: 'ok', text: `Change rejected for ${agentType}.` });
      await load();
    } catch (err) {
      setFlash({
        kind: 'err',
        text: err instanceof Error ? err.message : 'Failed to reject change',
      });
    } finally {
      setBusy((b) => ({ ...b, [changeId]: false }));
    }
  }

  // ── BYOLLM handlers (CFG.LLM.A2) ──
  //
  // saveByollm validates URL on the client too (defence in depth — the API
  // also enforces HTTPS) and surfaces the API's "endpoint unreachable" error
  // verbatim on save failure. The draft state is cleared on success so the
  // password input goes back to empty.

  function isHttpsUrl(s: string): boolean {
    try {
      const u = new URL(s);
      return u.protocol === 'https:';
    } catch {
      return false;
    }
  }

  async function saveByollm(agentType: PhaseAgentType) {
    const endpoint = (byollmEndpointDraft[agentType] ?? '').trim();
    const apiKey = byollmKeyDraft[agentType] ?? '';
    if (!endpoint || !apiKey) {
      setFlash({ kind: 'err', text: 'Both endpoint URL and API key are required.' });
      return;
    }
    if (!isHttpsUrl(endpoint)) {
      setFlash({ kind: 'err', text: 'Endpoint must be a valid HTTPS URL.' });
      return;
    }
    const busyKey = `byollm:${agentType}`;
    setBusy((b) => ({ ...b, [busyKey]: true }));
    try {
      await apiFetch(`/api/config/agents/${agentType}/byollm`, {
        method: 'PATCH',
        body: JSON.stringify({ endpoint, apiKey }),
      });
      setFlash({ kind: 'ok', text: `Custom endpoint saved for ${agentType}.` });
      setByollmKeyDraft((d) => {
        const next = { ...d };
        delete next[agentType];
        return next;
      });
      await load();
    } catch (err) {
      setFlash({
        kind: 'err',
        text: err instanceof Error ? err.message : 'Failed to save custom endpoint',
      });
    } finally {
      setBusy((b) => ({ ...b, [busyKey]: false }));
    }
  }

  async function removeByollm(agentType: PhaseAgentType) {
    if (!window.confirm(`Remove the custom endpoint for ${agentType}? Inference will revert to the platform default model.`)) {
      return;
    }
    const busyKey = `byollm:${agentType}`;
    setBusy((b) => ({ ...b, [busyKey]: true }));
    try {
      await apiFetch(`/api/config/agents/${agentType}/byollm`, { method: 'DELETE' });
      setFlash({ kind: 'ok', text: `Custom endpoint removed for ${agentType}.` });
      setByollmEndpointDraft((d) => {
        const next = { ...d };
        delete next[agentType];
        return next;
      });
      setByollmKeyDraft((d) => {
        const next = { ...d };
        delete next[agentType];
        return next;
      });
      await load();
    } catch (err) {
      setFlash({
        kind: 'err',
        text: err instanceof Error ? err.message : 'Failed to remove custom endpoint',
      });
    } finally {
      setBusy((b) => ({ ...b, [busyKey]: false }));
    }
  }

  // Canonical order — driven by PHASE_AGENTS so the UI never drifts from the
  // shared enum. The API response already provides every PHASE_AGENT entry,
  // so we index it and walk PHASE_AGENTS.
  const byType = new Map(rows.map((r) => [r.agentType, r] as const));

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg)' }}>
      <Sidebar />
      <main style={{ flex: 1, padding: 32, maxWidth: 960 }}>
        <div style={{ marginBottom: 16 }}>
          <Link
            to="/settings"
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 13,
              color: 'var(--text-secondary)',
              textDecoration: 'none',
            }}
          >
            ← Back to settings
          </Link>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 24,
              fontWeight: 700,
              color: 'var(--text)',
              margin: 0,
            }}
          >
            Per-Agent Model Configuration
          </h1>
          <HelpPopover
            id="agent-model-selection"
            variant="info"
            heading="Choosing agent models"
            body={
              <>
                <p style={{ margin: '0 0 8px' }}>
                  Different agents benefit from different models. Architecture and Code Review agents use deep reasoning heavily; transcript and summarisation agents do not.
                </p>
                <p style={{ margin: '0 0 6px' }}>
                  <strong>Claude Opus 4.6</strong> — best reasoning quality, highest cost, slowest. Use for agents where decision quality dominates.
                </p>
                <p style={{ margin: '0 0 6px' }}>
                  <strong>Claude Sonnet 4.6</strong> — balanced quality, cost, and speed. The default for most agents.
                </p>
                <p style={{ margin: '0 0 8px' }}>
                  <strong>Claude Haiku 4.5</strong> — fastest and cheapest. Use for high-volume, low-judgement work.
                </p>
                <p style={{ margin: 0 }}>
                  Model changes don&apos;t take effect until approved by a platform admin. Propose your changes here; they activate after approval.
                </p>
              </>
            }
          />
        </div>
        <p
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 14,
            color: 'var(--text-secondary)',
            marginBottom: 24,
          }}
        >
          Select which LLM model each phase agent uses. Changes require approval from a
          tenant admin or a platform admin before they take effect.
        </p>

        {flash && (
          <div
            style={{
              ...cardStyle,
              padding: '10px 14px',
              background: flash.kind === 'ok' ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)',
              borderColor: flash.kind === 'ok' ? 'rgba(16,185,129,0.4)' : 'rgba(239,68,68,0.4)',
              color: flash.kind === 'ok' ? '#10b981' : '#ef4444',
              fontFamily: 'var(--font-body)',
              fontSize: 13,
            }}
          >
            {flash.text}
          </div>
        )}

        {(loading || tenantLoading) && (
          <div style={{ ...cardStyle, fontFamily: 'var(--font-body)', color: 'var(--text-secondary)' }}>
            Loading…
          </div>
        )}

        {error && (
          <div style={{ ...cardStyle, borderColor: '#ef4444', color: '#ef4444' }}>{error}</div>
        )}

        {!loading && !error && PHASE_AGENTS.map((meta) => {
          const row = byType.get(meta.agentType);
          if (!row) return null;
          const draft = drafts[row.agentType] ?? row.activeModel;
          const canPropose = isAdmin && !row.pendingChange && draft !== row.activeModel;
          const isBusy = busy[row.agentType];
          return (
            <div key={row.agentType} style={cardStyle}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 4,
                }}
              >
                <h2
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 16,
                    fontWeight: 600,
                    color: 'var(--text)',
                  }}
                >
                  Phase {row.phase} — {row.name}
                </h2>
                <span
                  style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: 11,
                    color: 'var(--text-secondary)',
                    border: '1px solid var(--border)',
                    borderRadius: 4,
                    padding: '2px 6px',
                  }}
                >
                  {row.agentType}
                </span>
                {row.source === 'default' && (
                  <span
                    style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: 11,
                      color: 'var(--text-secondary)',
                    }}
                  >
                    (default)
                  </span>
                )}
              </div>

              <div style={{ marginBottom: 12 }}>
                <div style={labelStyle}>Active model</div>
                <div
                  style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: 14,
                    color: 'var(--text)',
                  }}
                >
                  {modelLabel(row.activeModel)}
                </div>
              </div>

              {row.pendingChange ? (
                <div
                  style={{
                    padding: 12,
                    background: 'rgba(245,158,11,0.08)',
                    border: '1px solid rgba(245,158,11,0.4)',
                    borderRadius: 'var(--radius-sm)',
                    marginBottom: 8,
                  }}
                >
                  <div
                    style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: 13,
                      color: 'var(--text)',
                      marginBottom: 8,
                    }}
                  >
                    <strong>Pending proposal:</strong> {modelLabel(row.pendingChange.fromModel ?? row.activeModel)}{' '}
                    → {modelLabel(row.pendingChange.toModel)}
                  </div>
                  <div
                    style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: 12,
                      color: 'var(--text-secondary)',
                      marginBottom: 8,
                    }}
                  >
                    Proposed by {proposerLabel(row.pendingChange)} at{' '}
                    {new Date(row.pendingChange.proposedAt).toLocaleString()}
                  </div>
                  {isAdmin && (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        disabled={busy[row.pendingChange.changeId]}
                        onClick={() => approve(row.pendingChange!.changeId, row.agentType)}
                        style={primaryButton}
                      >
                        Approve
                      </button>
                      <button
                        disabled={busy[row.pendingChange.changeId]}
                        onClick={() => reject(row.pendingChange!.changeId, row.agentType)}
                        style={ghostButton}
                      >
                        Reject
                      </button>
                    </div>
                  )}
                  {!isAdmin && (
                    <div style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>
                      Awaiting tenant admin review.
                    </div>
                  )}
                </div>
              ) : isAdmin ? (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <select
                    value={draft}
                    onChange={(e) => {
                      const v = e.target.value as AvailableModel;
                      setDrafts((d) => ({ ...d, [row.agentType]: v }));
                    }}
                    style={selectStyle}
                  >
                    {AVAILABLE_MODELS.map((m) => (
                      <option key={m} value={m}>{modelLabel(m)}</option>
                    ))}
                  </select>
                  <button
                    disabled={!canPropose || isBusy}
                    onClick={() => propose(row.agentType)}
                    style={{
                      ...primaryButton,
                      opacity: canPropose && !isBusy ? 1 : 0.5,
                      cursor: canPropose && !isBusy ? 'pointer' : 'not-allowed',
                    }}
                  >
                    {isBusy ? 'Submitting…' : 'Propose change'}
                  </button>
                </div>
              ) : (
                <div style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>
                  Only tenant admins can propose changes.
                </div>
              )}

              {/* CFG.LLM.A2 — Custom endpoint (BYOLLM) collapsible. Admin-only.
                  Plaintext key never round-trips: the API stores ciphertext
                  and only ever returns hasByollmKey as a boolean. */}
              {isAdmin && (
                <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                  <button
                    type="button"
                    onClick={() =>
                      setByollmExpanded((m) => ({ ...m, [row.agentType]: !m[row.agentType] }))
                    }
                    style={{
                      ...ghostButton,
                      padding: '6px 10px',
                      fontSize: 12,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <span>{byollmExpanded[row.agentType] ? '▾' : '▸'}</span>
                    Custom endpoint
                    {row.byollmEndpoint && (
                      <span
                        style={{
                          fontFamily: 'var(--font-body)',
                          fontSize: 11,
                          color: '#10b981',
                          marginLeft: 4,
                        }}
                      >
                        ● configured
                      </span>
                    )}
                  </button>

                  {byollmExpanded[row.agentType] && (
                    <div
                      style={{
                        marginTop: 12,
                        padding: 12,
                        background: 'var(--bg)',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-sm)',
                      }}
                    >
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ ...labelStyle, marginBottom: 4 }}>Endpoint URL (HTTPS)</div>
                        <input
                          type="url"
                          placeholder="https://llm.example.com/v1"
                          value={byollmEndpointDraft[row.agentType] ?? row.byollmEndpoint ?? ''}
                          onChange={(e) =>
                            setByollmEndpointDraft((d) => ({
                              ...d,
                              [row.agentType]: e.target.value,
                            }))
                          }
                          style={{
                            ...selectStyle,
                            width: '100%',
                            minWidth: 0,
                          }}
                        />
                      </div>
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ ...labelStyle, marginBottom: 4 }}>
                          API Key{' '}
                          {row.hasByollmKey && (
                            <span
                              style={{
                                color: 'var(--text-secondary)',
                                textTransform: 'none',
                                letterSpacing: 0,
                                fontSize: 11,
                              }}
                            >
                              (a key is configured — leave blank to keep, or paste a new one to replace)
                            </span>
                          )}
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: 'var(--text-secondary)',
                            marginBottom: 6,
                            textTransform: 'none',
                            letterSpacing: 0,
                          }}
                        >
                          Need a key?{' '}
                          <a
                            href="https://platform.openai.com/api-keys"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: 'var(--primary)' }}
                          >
                            OpenAI
                          </a>
                          {' · '}
                          <a
                            href="https://console.anthropic.com/settings/keys"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: 'var(--primary)' }}
                          >
                            Anthropic
                          </a>
                          {' · '}
                          <a
                            href="https://portal.azure.com/#blade/Microsoft_Azure_ProjectOxford/CognitiveServicesHub/OpenAI"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: 'var(--primary)' }}
                          >
                            Azure OpenAI
                          </a>
                        </div>
                        <PasswordInput
                          placeholder={row.hasByollmKey ? '••••••••' : 'sk-...'}
                          value={byollmKeyDraft[row.agentType] ?? ''}
                          onChange={(e) =>
                            setByollmKeyDraft((d) => ({
                              ...d,
                              [row.agentType]: e.target.value,
                            }))
                          }
                          autoComplete="new-password"
                          wrapperStyle={{ width: '100%', minWidth: 0 }}
                          style={{
                            ...selectStyle,
                            width: '100%',
                            minWidth: 0,
                          }}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          type="button"
                          disabled={busy[`byollm:${row.agentType}`]}
                          onClick={() => saveByollm(row.agentType)}
                          style={{
                            ...primaryButton,
                            opacity: busy[`byollm:${row.agentType}`] ? 0.5 : 1,
                          }}
                        >
                          {busy[`byollm:${row.agentType}`] ? 'Saving…' : 'Save endpoint'}
                        </button>
                        {row.byollmEndpoint && (
                          <button
                            type="button"
                            disabled={busy[`byollm:${row.agentType}`]}
                            onClick={() => removeByollm(row.agentType)}
                            style={ghostButton}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      <div
                        style={{
                          marginTop: 8,
                          fontFamily: 'var(--font-body)',
                          fontSize: 11,
                          color: 'var(--text-secondary)',
                        }}
                      >
                        Custom endpoints take effect immediately — no approval flow. The platform
                        default is used as a fallback if your endpoint errors at request time.
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </main>
    </div>
  );
}
